import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  MessageCircle, Users, GraduationCap, AlertCircle, Plus, Link2,
  Copy, Trash2, Edit3, Loader2, X, Search, Check, Eye,
} from 'lucide-react';
import { api } from '../lib/api';
import DoubtChatView from '../components/DoubtChatView';
import { authService } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DoubtEngineAdmin() {
  const [tab, setTab] = useState('overview'); // 'overview' | 'batches' | 'chats'
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [chats, setChats] = useState([]);
  const [chatFilter, setChatFilter] = useState({ batch_id: '', teacher_id: '', pending_only: false });
  const [showCreate, setShowCreate] = useState(false);
  const [editBatch, setEditBatch] = useState(null);
  const [openChat, setOpenChat] = useState(null); // {chat, messages}

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, batchesRes, coursesRes, teachersRes] = await Promise.all([
        api.getDoubtStats(),
        api.getDoubtBatches(),
        api.getCoursesList(),
        api.getTeachers(),
      ]);
      setStats(statsRes.data);
      setBatches(batchesRes.data || []);
      setCourses(coursesRes.data || []);
      setTeachers(teachersRes.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Doubt Engine');
    } finally { setLoading(false); }
  };

  const fetchChats = async () => {
    try {
      const params = {};
      if (chatFilter.batch_id) params.batch_id = chatFilter.batch_id;
      if (chatFilter.teacher_id) params.teacher_id = chatFilter.teacher_id;
      if (chatFilter.pending_only) params.pending_only = 'true';
      const res = await api.getDoubtChats(params);
      setChats(res.data || []);
    } catch (e) {
      toast.error('Failed to load chats');
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (tab === 'chats') fetchChats(); }, [tab, chatFilter]);

  const publicLink = (slug) => `${window.location.origin}/doubt/${slug}`;

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(publicLink(slug));
      toast.success('Link copied');
    } catch (e) { toast.error('Copy failed'); }
  };

  const handleDelete = async (batchId) => {
    if (!window.confirm('Deactivate this batch? Students will no longer be able to open the link.')) return;
    try {
      await api.deleteDoubtBatch(batchId);
      toast.success('Batch deactivated');
      fetchAll();
    } catch (e) { toast.error('Delete failed'); }
  };

  const openChatModal = async (chat) => {
    try {
      const res = await api.getDoubtChatMessages(chat.id);
      setOpenChat({ chat, messages: res.data.messages || [] });
    } catch (e) { toast.error('Failed to open chat'); }
  };

  const wsUrl = (chatId) => {
    if (!chatId) return null;
    const token = authService.getToken();
    if (!token) return null;
    const base = BACKEND_URL.replace(/^http/, 'ws');
    return `${base}/api/doubt-engine/ws/${chatId}?token=${encodeURIComponent(token)}&role=teacher`;
  };

  const statCards = useMemo(() => ([
    { label: 'Active Batches', value: stats?.total_batches ?? 0, icon: GraduationCap, color: 'from-blue-500 to-blue-700' },
    { label: 'Total Chats', value: stats?.total_chats ?? 0, icon: MessageCircle, color: 'from-emerald-500 to-emerald-700' },
    { label: 'Pending Reply', value: stats?.pending_reply_chats ?? 0, icon: AlertCircle, color: 'from-rose-500 to-rose-700' },
    { label: 'Assigned Teachers', value: stats?.per_teacher?.length ?? 0, icon: Users, color: 'from-indigo-500 to-indigo-700' },
  ]), [stats]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  }

  return (
    <div data-testid="doubt-engine-admin" className="max-w-[1600px] mx-auto space-y-6 px-2 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">Doubt Engine</h1>
          <p className="text-sm text-slate-600 mt-1">Batch-wise student ↔ teacher chat with WhatsApp-style media & voice notes.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          data-testid="doubt-create-batch-btn"
        >
          <Plus size={18} /> New Doubt Batch
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'batches', label: `Batches (${batches.length})` },
          { id: 'chats', label: 'All Chats' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`doubt-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {statCards.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className={`rounded-xl p-4 bg-gradient-to-br ${s.color} text-white shadow-lg`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase tracking-wider opacity-80">{s.label}</div>
                      <div className="text-3xl font-bold mt-1">{s.value}</div>
                    </div>
                    <Icon size={32} className="opacity-40" />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Users size={16} /> Pending by Teacher</div>
              {(stats?.per_teacher || []).length === 0 ? (
                <div className="text-sm text-slate-500">No pending replies</div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {stats.per_teacher.map((t) => (
                    <div key={t.teacher_id} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                      <span className="text-slate-700">{t.teacher_name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">{t.pending_chats} chats</span>
                        <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-medium">{t.pending_messages} pending</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><GraduationCap size={16} /> Pending by Batch</div>
              {(stats?.per_batch || []).length === 0 ? (
                <div className="text-sm text-slate-500">No pending replies</div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {stats.per_batch.map((b) => (
                    <div key={b.batch_id} className="flex items-center justify-between text-sm py-2 border-b last:border-0">
                      <span className="text-slate-700 truncate max-w-[75%]">{b.course_name}</span>
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">{b.pending_chats} pending</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {tab === 'batches' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <th className="px-4 py-3">Course (Batch)</th>
                <th className="px-4 py-3">Teachers</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Public Link</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {batches.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-16 text-center text-slate-500">
                  <MessageCircle size={40} className="mx-auto mb-2 text-slate-300" />
                  No doubt batches yet. Create one to start.
                </td></tr>
              )}
              {batches.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50" data-testid={`doubt-batch-row-${b.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{b.course_name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    <div className="flex flex-wrap gap-1">
                      {(b.allowed_teachers || []).slice(0, 4).map((t) => (
                        <span key={t.id} className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs">{t.name}</span>
                      ))}
                      {(b.allowed_teachers || []).length > 4 && (
                        <span className="text-xs text-slate-400">+{b.allowed_teachers.length - 4} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${b.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                      {b.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded truncate max-w-[220px]">/doubt/{b.slug}</code>
                      <button onClick={() => copyLink(b.slug)}
                              className="p-1 hover:bg-slate-200 rounded"
                              data-testid={`doubt-copy-link-${b.id}`}
                              title="Copy full URL">
                        <Copy size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button onClick={() => setEditBatch(b)} className="p-2 hover:bg-slate-100 rounded" title="Edit">
                      <Edit3 size={16} />
                    </button>
                    <button onClick={() => handleDelete(b.id)} className="p-2 hover:bg-rose-50 rounded text-rose-600" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'chats' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
            <select
              value={chatFilter.batch_id}
              onChange={(e) => setChatFilter((f) => ({ ...f, batch_id: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              data-testid="doubt-chat-batch-filter"
            >
              <option value="">All Batches</option>
              {batches.map((b) => <option key={b.id} value={b.id}>{b.course_name}</option>)}
            </select>
            <select
              value={chatFilter.teacher_id}
              onChange={(e) => setChatFilter((f) => ({ ...f, teacher_id: e.target.value }))}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Teachers</option>
              {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={chatFilter.pending_only}
                     onChange={(e) => setChatFilter((f) => ({ ...f, pending_only: e.target.checked }))}
                     data-testid="doubt-chat-pending-only" />
              Pending reply only
            </label>
            <div className="text-sm text-slate-500 flex items-center">Showing {chats.length} chats</div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
            {chats.length === 0 && (
              <div className="p-10 text-center text-slate-500 text-sm">No chats match your filters</div>
            )}
            {chats.map((c) => (
              <div key={c.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900">{c.student_name} <span className="text-slate-400 font-normal">· {c.student_phone}</span></div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.course_name} · with {c.teacher_name}
                  </div>
                  <div className="text-xs text-slate-400 truncate mt-0.5">{c.last_message_preview}</div>
                </div>
                <div className="flex items-center gap-3">
                  {c.teacher_unread_count > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-medium">
                      {c.teacher_unread_count} unread
                    </span>
                  )}
                  <button onClick={() => openChatModal(c)} className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-900" data-testid={`doubt-open-chat-${c.id}`}>
                    <Eye size={14} /> Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {(showCreate || editBatch) && (
        <BatchFormModal
          courses={courses}
          teachers={teachers}
          batch={editBatch}
          onClose={() => { setShowCreate(false); setEditBatch(null); }}
          onSaved={() => { setShowCreate(false); setEditBatch(null); fetchAll(); }}
        />
      )}

      {/* Chat viewer modal */}
      {openChat && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
          <div className="bg-slate-900 rounded-2xl w-full max-w-2xl h-[85vh] flex flex-col overflow-hidden shadow-2xl">
            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
              <div className="text-white font-semibold text-sm">
                {openChat.chat.student_name} · {openChat.chat.course_name}
              </div>
              <button className="text-slate-300 hover:text-white" onClick={() => setOpenChat(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <DoubtChatView
                chatId={openChat.chat.id}
                wsUrl={wsUrl(openChat.chat.id)}
                identity="teacher"
                initialMessages={openChat.messages}
                header={{ title: openChat.chat.student_name, subtitle: `${openChat.chat.student_phone} · with ${openChat.chat.teacher_name}` }}
                onSendText={async (content) => (await api.sendDoubtStaffText(openChat.chat.id, content)).data}
                onSendMedia={async (file, mt) => (await api.sendDoubtStaffMedia(openChat.chat.id, file, mt)).data}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BatchFormModal({ courses, teachers, batch, onClose, onSaved }) {
  const [courseId, setCourseId] = useState(batch?.course_id || '');
  const [selected, setSelected] = useState(batch?.allowed_teacher_ids || []);
  const [welcome, setWelcome] = useState(batch?.welcome_message || 'Hi! Ask your doubts here.');
  const [isActive, setIsActive] = useState(batch?.is_active !== false);
  const [saving, setSaving] = useState(false);
  const [tSearch, setTSearch] = useState('');

  const filteredTeachers = useMemo(() => {
    const q = tSearch.toLowerCase();
    return teachers.filter((t) => !q || (t.name || '').toLowerCase().includes(q) || (t.email || '').toLowerCase().includes(q));
  }, [teachers, tSearch]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    if (!courseId) { toast.error('Pick a course/batch'); return; }
    if (selected.length === 0) { toast.error('Pick at least one teacher'); return; }
    setSaving(true);
    try {
      if (batch) {
        await api.updateDoubtBatch(batch.id, {
          allowed_teacher_ids: selected,
          welcome_message: welcome,
          is_active: isActive,
        });
        toast.success('Batch updated');
      } else {
        await api.createDoubtBatch({
          course_id: courseId,
          allowed_teacher_ids: selected,
          welcome_message: welcome,
          is_active: isActive,
        });
        toast.success('Batch created');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" data-testid="doubt-batch-modal">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">{batch ? 'Edit' : 'Create'} Doubt Batch</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Course / Batch</label>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              disabled={!!batch}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:bg-slate-100"
              data-testid="doubt-batch-course"
            >
              <option value="">Select a course…</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Welcome message shown to students</label>
            <textarea rows={2} value={welcome} onChange={(e) => setWelcome(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Hi! Ask your doubts here." />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">Teachers available in this batch</label>
              <span className="text-xs text-slate-500">{selected.length} selected</span>
            </div>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={tSearch} onChange={(e) => setTSearch(e.target.value)}
                     placeholder="Search teachers…"
                     className="w-full pl-7 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg" />
            </div>
            <div className="border border-slate-200 rounded-lg divide-y max-h-56 overflow-y-auto">
              {filteredTeachers.length === 0 && (
                <div className="p-3 text-xs text-slate-500">No teachers found. Create teachers via User Management.</div>
              )}
              {filteredTeachers.map((t) => {
                const on = selected.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggle(t.id)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50"
                    data-testid={`doubt-teacher-toggle-${t.id}`}
                  >
                    <span className="truncate text-left">
                      <span className="text-slate-900">{t.name}</span>
                      <span className="text-slate-400"> · {t.email}</span>
                    </span>
                    <span className={`w-5 h-5 rounded border flex items-center justify-center ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300'}`}>
                      {on && <Check size={14} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active (public link works)
          </label>
        </div>
        <div className="p-4 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-60"
                  data-testid="doubt-batch-save-btn">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Link2 size={16} />}
            {batch ? 'Save changes' : 'Create batch'}
          </button>
        </div>
      </div>
    </div>
  );
}
